import './contactDetailsComponent.scss'
import React, {useCallback, useEffect, useRef, useState} from 'react';
import FormTitleWithNavigationBack
    from '../../../../shared/components/form-title-with-navigation-back/FormTitleWithNavigationBack';
import {Field, FieldProps, Form, Formik, FormikHelpers, FormikProps, FormikValues} from 'formik';
import * as Yup from "yup";
import FormikInputComponent from '../../../../shared/components/core/formik-input/FormikInputComponent';
import {Grid} from '@mui/material';
import FormikAutoCompleteDropdown
    from '../../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown';
import ButtonComponent from '../../../../shared/components/core/button/ButtonComponent';
import {CommonService} from '../../../../shared/services';
import {IAPIResponseType} from '../../../../shared/models/api.model';
import {APIConfig} from '../../../../constants';
import FromikPhoneNumberComponent
    from "../../../../shared/components/core/fromik-phone-number/FromikPhoneNumberComponent";

interface PrimaryContactDetailsComponentProps {
    next: Function,
    patientID?: number,
    patientDetails: any
    cancel?: Function,
}

const validationSchema = Yup.object({
    emailId: Yup.string()
        .email("Invalid Email ID")
        .nullable()
        .matches(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/, "Enter a valid Email ID"),
    businessEmailId: Yup.string()
        .email("Invalid Business Email ID")
        .nullable()
        .matches(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/, "Enter a valid Business Email ID"),
    businessMobileNumber: Yup.string()
        .nullable()
        .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),
    mobileNumber: Yup.string()
        .nullable()
        .required("Mobile Number is Required")
        .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),
    secondaryMobileNumber: Yup.string()
        .nullable()
        .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),
    addressLine1: Yup.string()
        .nullable()
        .required("Address Line 1 is required")
        .max(200, 'Address Line 1 cannot exceed 200 characters'),
    addressLine2: Yup.string()
        .nullable()
        .max(200, 'Address Line 2 cannot exceed 200 characters'),
    zipcode: Yup.mixed()
        .nullable()
        .required("Zip Code is required"),
    state: Yup.mixed()
        .required("State is required")
        .nullable(),
    city: Yup.mixed()
        .required("City is required")
        .nullable(),
    county: Yup.mixed()
        .required("County is required")
        .nullable(),
    country: Yup.mixed()
        .required("Country is required")
        .nullable(),
    newZipCode: Yup.string().when('zipcode', {
        is: (zipcode: any) => (zipcode?.zipcode && zipcode?.zipcode?.toLowerCase() === "other"),
        then: Yup.string().required('Zip Code is required')
            .matches(/^[1-9]\d{4}$/, {message: "Please Enter a Valid Zipcode"})
            .max(5, "Zipcode should be 5 digits")
            .min(5, "Zipcode should be 5 digits")
            .nullable(),
    }),
});

interface CreatePatientAdditionalDetailsFormSchema {
    businessEmailId: string,
    businessMobileNumber: string,
    mobileNumber: string,
    secondaryMobileNumber: string,
    emailId: string,
    addressLine1: string,
    addressLine2: string,
    zipcode: any,
    newZipCode: string,
    state: string,
    city: string,
    county: string,
    country: string,
}

const formInitialValues: CreatePatientAdditionalDetailsFormSchema = {
    mobileNumber: "",
    secondaryMobileNumber: "",
    emailId: "",
    businessEmailId: "",
    businessMobileNumber: "",
    addressLine1: "",
    addressLine2: "",
    zipcode: {},
    newZipCode: "",
    state: "",
    city: "",
    county: "",
    country: "",
}


const ContactDetailsComponent = (props: PrimaryContactDetailsComponentProps) => {
        const {next, patientID, cancel, patientDetails} = props;
        const formikRef = useRef<FormikProps<FormikValues>>(null);

        const [state, setState] = useState<string>("");

        const [isZipCodeNotFound, setIsZipCodeNotFound] = useState<boolean>(false);
        const [initialValues, setInitialValues] = useState<CreatePatientAdditionalDetailsFormSchema>(formInitialValues);

        const handleZipCodeValueChange = useCallback((zipCodeObject: any, setFieldValue: any) => {
            if (zipCodeObject === "") {
                setFieldValue("state", "");
                setFieldValue("city", "");
                setFieldValue("county", "");
                setFieldValue("country", "");
                setIsZipCodeNotFound(false);
                setState("");
            } else {
                if (zipCodeObject?.zipcode?.toLowerCase() === "other") {
                    setIsZipCodeNotFound(true);
                    setFieldValue("state", "");
                    setFieldValue("city", "");
                    setFieldValue("county", "");
                    setFieldValue("country", "USA"); // set default country to USA
                } else {
                    setIsZipCodeNotFound(false);
                    setFieldValue("state", zipCodeObject.state);
                    setFieldValue("city", zipCodeObject.city);
                    setFieldValue("county", zipCodeObject.county);
                    setFieldValue("country", zipCodeObject.country);
                }
            }
            setFieldValue("newZipCode", "");
        }, []);

        useEffect(() => {
            if (patientDetails?.zipcode) {
                const patientInformationDetails = {...patientDetails};
                if (!patientInformationDetails.zipcodeDetails) {
                    patientInformationDetails.newZipCode = patientInformationDetails?.zipcode;
                    patientInformationDetails.zipcode = {
                        zipcode: "Other"
                    };
                    patientInformationDetails.state = patientInformationDetails?.stateDetails;
                    patientInformationDetails.city = patientInformationDetails?.cityDetails;
                    setIsZipCodeNotFound(true);
                } else {
                    patientInformationDetails.zipcode = patientInformationDetails.zipcodeDetails;
                    setIsZipCodeNotFound(false);
                }
                setState(patientInformationDetails.state);
                setInitialValues(patientInformationDetails);
            }
        }, [patientDetails]);


        const onFormSubmit = useCallback((values: any, {setSubmitting, setErrors, validateForm}: FormikHelpers<any>) => {
            const payload = {...values};
            payload.mobileNumber = payload.mobileNumber?.replace(/[^0-9]/g, "");
            payload.secondaryMobileNumber = payload.secondaryMobileNumber?.replace(/[^0-9]/g, "");
            payload.businessMobileNumber = payload.businessMobileNumber?.replace(/[^0-9]/g, "");
            if (payload["newZipCode"]) {
                payload["zipcode"] = payload["newZipCode"];
                payload["state"] = payload?.state?.state;
                payload["city"] = payload?.city?.city;
                payload["county"] = payload?.county?.county;
                delete payload["newZipCode"];
            } else {
                payload["zipcode"] = payload?.zipcode?.zipcode;
            }
            setSubmitting(true);
            CommonService._patient.addPatientContactDetails(patientID, payload)
                .then((response: IAPIResponseType<any>) => {
                    setSubmitting(false);
                    next(response.data);
                })
                .catch((error: any) => {
                    CommonService.handleErrors(setErrors, error);
                    setSubmitting(false);
                })

        }, [patientID, next]);

        return (
            <div className="patient-contact-details-screen screen t-form-screen">

                <FormTitleWithNavigationBack
                    title={'Contact Details'}
                    tooltipData={"This includes the necessary information to contact the patient. Ensure that the inputs provided are accurate."}
                />
                <div className="t-form">
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        validateOnChange={true}
                        validateOnBlur={true}
                        validateOnMount={true}
                        enableReinitialize={true}
                        innerRef={formikRef}
                        onSubmit={onFormSubmit}
                    >
                        {({isSubmitting, values, isValid, touched, setFieldValue, validateForm, errors}) => {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            useEffect(() => {
                                validateForm();
                            }, [validateForm, values]);
                            return (
                                <Form className="patient-contact-details-add-holder">
                                    <div className="t-form-controls-wrapper">
                                        <Field name={'emailId'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Email ID'}
                                                        placeholder={'Enter Email ID'}
                                                        type={"email"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'businessEmailId'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Business Email ID'}
                                                                placeholder={'Enter Business Email ID'}
                                                                type={"email"}
                                                                required={false}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'businessMobileNumber'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FromikPhoneNumberComponent
                                                                label={'Business Mobile Number'}
                                                                placeholder={'(000) 000-0000'}
                                                                maxLength={14}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'mobileNumber'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FromikPhoneNumberComponent
                                                                label={'Mobile Number'}
                                                                placeholder={'(000) 000-0000'}
                                                                maxLength={14}
                                                                required={true}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'secondaryMobileNumber'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FromikPhoneNumberComponent
                                                                label={'Secondary Mobile Number'}
                                                                placeholder={'(000) 000-0000'}
                                                                maxLength={14}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>
                                        <div className="horizontal-line"/>
                                        <div className='form-sub-title'>Address Details :</div>
                                        <Field name={'addressLine1'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Address Line 1'}
                                                        placeholder={'Enter Address Line 1'}
                                                        type={"text"}
                                                        required={true}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'addressLine2'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Address Line 2'}
                                                                placeholder={'Enter Address Line 2'}
                                                                type={"text"}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'zipcode'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikAutoCompleteDropdown
                                                                label={'ZIP Code'}
                                                                placeholder={'Start typing for ZIP Code'}
                                                                required={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                                openOnFocus={true}
                                                                url={APIConfig.ZIPCODE_LIST.URL}
                                                                method={APIConfig.ZIPCODE_LIST.METHOD}
                                                                searchMode={'serverSide'}
                                                                data={[]}
                                                                valueKey={'zipcode'}
                                                                dataListKey={"data.docs"}
                                                                onUpdate={(value: any) => {
                                                                    handleZipCodeValueChange(value, setFieldValue)
                                                                }}
                                                                //Hiding other option from zipcode
                                                                // defaultData={[
                                                                //     {
                                                                //         zipcode: "Other"
                                                                //     }
                                                                // ]}
                                                                getOptionLabel={(option => (option.zipcode) || '')}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>
                                        {isZipCodeNotFound && <>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Field name={'newZipCode'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    placeholder={"Enter New ZIP Code"}
                                                                    label={'New ZIP Code'}
                                                                    required={true}
                                                                    maxLength={5}
                                                                    type={"text"}
                                                                    formikField={field}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Field name={'state'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikAutoCompleteDropdown
                                                                    label={'State'}
                                                                    required={true}
                                                                    formikField={field}
                                                                    url={APIConfig.STATE_LIST.URL}
                                                                    method={APIConfig.STATE_LIST.METHOD}
                                                                    searchMode={'serverSide'}
                                                                    openOnFocus={true}
                                                                    dataListKey={"data.docs"}
                                                                    data={[]}
                                                                    valueKey={'state'}
                                                                    onUpdate={(value: any) => {
                                                                        setState(value?.state);
                                                                        field.form.setFieldValue("city", '');
                                                                    }}
                                                                    getOptionLabel={(option => (option.state) || '')}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </Grid>
                                            </Grid>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Field name={'city'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikAutoCompleteDropdown
                                                                    label={'City'}
                                                                    placeholder={'City'}
                                                                    required={true}
                                                                    valueKey={'city'}
                                                                    id="city_auto_complete"
                                                                    formikField={field}
                                                                    searchMode={'serverSide'}
                                                                    url={APIConfig.CITY_LIST.URL}
                                                                    method={APIConfig.CITY_LIST.METHOD}
                                                                    disabled={!state}
                                                                    payload={{
                                                                        state: state,
                                                                        page: 1,
                                                                        limit: 100
                                                                    }}
                                                                    data={[]}
                                                                    getOptionLabel={(option => (option.city) || '')}
                                                                    dataListKey={"data.docs"}
                                                                />
                                                            )
                                                        }
                                                    </Field>

                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    {/*<Field name={'timezone'}>*/}
                                                    {/*    {*/}
                                                    {/*        (field: FieldProps) => (*/}
                                                    {/*            <FormikAutoCompleteDropdown*/}
                                                    {/*                label={'Timezone'}*/}
                                                    {/*                placeholder={'Timezone'}*/}
                                                    {/*                required={true}*/}
                                                    {/*                id="timezone_auto_complete"*/}
                                                    {/*                formikField={field}*/}
                                                    {/*                searchMode={'serverSide'}*/}
                                                    {/*                url={APIConfig.CITY_LIST.URL}*/}
                                                    {/*                method={APIConfig.CITY_LIST.METHOD}*/}
                                                    {/*                disabled={!state}*/}
                                                    {/*                payload={{*/}
                                                    {/*                    state: state,*/}
                                                    {/*                    page: 1,*/}
                                                    {/*                    limit: 100*/}
                                                    {/*                }}*/}
                                                    {/*                data={[]}*/}
                                                    {/*                getOptionLabel={(option => (option.city) || '')}*/}
                                                    {/*                dataListKey={"data.docs"}*/}
                                                    {/*            />*/}
                                                    {/*        )*/}
                                                    {/*    }*/}
                                                    {/*</Field>*/}
                                                    <Field name={'county'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikAutoCompleteDropdown
                                                                    label={'County'}
                                                                    placeholder={'County'}
                                                                    required={true}
                                                                    valueKey={'county'}
                                                                    id="county_auto_complete"
                                                                    formikField={field}
                                                                    searchMode={'serverSide'}
                                                                    url={APIConfig.COUNTY_LIST.URL}
                                                                    method={APIConfig.COUNTY_LIST.METHOD}
                                                                    disabled={!state}
                                                                    payload={{
                                                                        state: state,
                                                                        page: 1,
                                                                        limit: 100
                                                                    }}
                                                                    data={[]}
                                                                    getOptionLabel={(option => (option.county) || '')}
                                                                    dataListKey={"data.docs"}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </Grid>
                                            </Grid>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Field name={'country'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    placeholder={"Enter Country"}
                                                                    label={'Country'}
                                                                    required={true}
                                                                    readOnly={true}
                                                                    type={"text"}
                                                                    formikField={field}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </Grid>
                                            </Grid>
                                        </>}
                                        {
                                            !isZipCodeNotFound && values?.zipcode?.zipcode && values?.zipcode?.zipcode !== "Other" &&
                                            <>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field name={'state'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        label={'State'}
                                                                        placeholder={'Enter state'}
                                                                        required={true}
                                                                        readOnly={true}
                                                                        formikField={field}
                                                                        type={"text"}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field name={'county'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        label={'County'}
                                                                        placeholder={'Enter county*'}
                                                                        required={true}
                                                                        readOnly={true}
                                                                        formikField={field}
                                                                        type={"text"}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </Grid>
                                                </Grid>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field name={'city'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        label={'City'}
                                                                        placeholder={'Enter city*'}
                                                                        required={true}
                                                                        readOnly={true}
                                                                        formikField={field}
                                                                        type={"text"}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field name={'country'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        label={'Country'}
                                                                        placeholder={'Enter country*'}
                                                                        required={true}
                                                                        readOnly={true}
                                                                        formikField={field}
                                                                        type={"text"}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </Grid>
                                                </Grid>
                                            </>
                                        }
                                    </div>
                                    <div className='form-action-section'>
                                        <ButtonComponent
                                            type="button"
                                            color={"primary"}
                                            variant={"outlined"}
                                            fullWidth={true}
                                            disabled={isSubmitting}
                                            handleClick={cancel}
                                        >
                                            Close
                                        </ButtonComponent>
                                        <ButtonComponent
                                            color={"primary"}
                                            type={"submit"}
                                            variant={"contained"}
                                            disabled={!isValid}
                                            fullWidth={true}
                                            isLoading={isSubmitting}
                                        >
                                            Next
                                        </ButtonComponent>
                                    </div>
                                </Form>
                            )
                        }}
                    </Formik>

                </div>
            </div>)
    }

;

export default ContactDetailsComponent;
